'use client';

import { useEffect, useState } from 'react';
import { useElementOnScreen } from '@/hooks/intersectionObserver';

import { IDealsSectionContent } from '@/interfaces/homepage';
import { IconChevronsDown, IconClock } from '@tabler/icons-react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IconArrowSmallLeft } from '@/icons/IconArrowSmallLeft';
import { IconArrowSmallRight } from '@/icons/IconArrowSmallRight';
import { OfferCard } from './OfferCard';

const AUTOPLAY_DELAY = 5000;

export const LimitedTimeOffer = ({ content, deviceType }: { content: IDealsSectionContent; deviceType: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const syncEdges = (swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) {
      containerRef?.current?.swiper?.autoplay?.start();
    } else {
      containerRef?.current?.swiper?.autoplay?.stop();
    }
  }, [isVisible, containerRef]);

  const filteredItems = content.Items.filter((item) => item.Device === deviceType);

  if (!filteredItems.length) return null;

  return (
    <div
      data-testid="limited-time-offer"
      style={{ transform: isOpen ? 'translateY(0)' : 'translateY(100%)' }}
      className="fixed bottom-0 z-[39] w-full bg-white drop-shadow-[0_35px_30px_rgba(0,0,0,0.5)] transition-transform duration-300"
    >
      <button
        style={{ backgroundColor: content.BackgroundColor ?? 'white', color: content.TextColor ?? 'black' }}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="absolute left-1/2 top-[1px] flex max-w-[90vw] -translate-x-1/2 -translate-y-full items-center gap-3 rounded-b-none rounded-t-lg px-4 py-2 text-lg font-medium text-black sm:text-xl md:text-2xl"
      >
        <IconClock className="h-5 w-5 md:h-6 md:w-6" />
        <span className="truncate">{content.Title ?? 'Check Limited Time Offer Products'}</span>
        <div className={`${isOpen ? '' : 'animate-bounce'}`}>
          <IconChevronsDown className={`h-5 w-5 transition-transform duration-300 md:h-6 md:w-6 ${isOpen ? '' : 'rotate-180'}`} />
        </div>
      </button>

      <div className="mx-4 py-6 pb-4 md:pb-6 md:pt-10">
        <div className="relative max-h-[65vh] flex-1" data-testid="limited-time-offer-slider-container">
          <Swiper
            modules={[Navigation, Autoplay]}
            ref={containerRef}
            spaceBetween={16}
            slidesPerView={1}
            autoplay={{ delay: AUTOPLAY_DELAY }}
            breakpoints={{
              400: { slidesPerView: 2 },
              640: { slidesPerView: 3 },
              1024: { slidesPerView: 4 },
              1400: { slidesPerView: 5 },
              1700: { slidesPerView: 6 },
            }}
            className="content-slider"
            wrapperClass="flex"
            onSwiper={syncEdges}
            onSlideChange={syncEdges}
            onResize={syncEdges}
            onBreakpoint={syncEdges}
            onUpdate={syncEdges}
            data-testid="limited-time-offer-swiper"
          >
            {filteredItems.map((offer, index) => (
              <SwiperSlide key={`${offer.Title}-${index}`} style={{ height: 'auto' }}>
                <OfferCard offer={offer} />
              </SwiperSlide>
            ))}
          </Swiper>

          {!isBeginning && (
            <ArrowButton
              onClick={() => containerRef?.current?.swiper?.slidePrev()}
              className="absolute left-3 top-1/2 z-20 -translate-y-1/2"
              dataTestId="limited-time-offer-prev-arrow"
            >
              <IconArrowSmallLeft className="h-8 w-8 drop-shadow-[0_2px_3px_rgba(0,0,0,0.8)]" data-testid="limited-time-offer-prev-icon" />
            </ArrowButton>
          )}

          {!isEnd && (
            <ArrowButton
              onClick={() => containerRef?.current?.swiper?.slideNext()}
              className="absolute right-3 top-1/2 z-20 -translate-y-1/2"
              dataTestId="limited-time-offer-next-arrow"
            >
              <IconArrowSmallRight className="h-8 w-8 drop-shadow-[0_2px_3px_rgba(0,0,0,0.8)]" data-testid="limited-time-offer-next-icon" />
            </ArrowButton>
          )}
        </div>
      </div>
    </div>
  );
};

export const ArrowButton = ({ onClick, className, children, dataTestId }: any) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-none bg-transparent p-0 ${className} cursor-pointer transition-opacity duration-300 disabled:cursor-not-allowed disabled:opacity-40`}
      data-testid={dataTestId}
    >
      <div className="m-0 flex h-10 w-10 items-center justify-center bg-transparent text-white transition-all duration-300 ease-in">{children}</div>
    </button>
  );
};
